import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { PASSWORD_RULES, isPasswordStrong as checkPasswordStrong } from '@/lib/passwordRules';
import { getActiveRegionConfig, getPortalHeaders } from '@/lib/regionConfig';
import TurnstileWidget from '@/components/shared/TurnstileWidget';
import { isCaptchaPending } from '@/lib/turnstile';
import supabase from '@/lib/supabaseClient';
import { Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CompanySearch from '@/components/companies/CompanySearch';
import { companiesHouseApi } from '@/services/api';

// A failed look-up (rate limit, Companies House down) must not block sign-up: the name is still typed in.
const searchCompaniesQuietly = async (query) => {
  try {
    return await companiesHouseApi.publicSearch(query);
  } catch {
    return { items: [] };
  }
};

// Sign-up form shown inside the sliding auth panel (see AuthSlider). `active` is false while the
// sign-in side is showing, so only one Turnstile widget is mounted at a time.
const SignupForm = ({ active = true, onSwitch }) => {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const regionConfig = getActiveRegionConfig();

  const [fullName, setFullName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmSent, setConfirmSent] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaNonce, setCaptchaNonce] = useState(0);
  const [resendStatus, setResendStatus] = useState(''); // '', 'sending', 'sent', 'error'

  const passwordErrors = password
    ? PASSWORD_RULES.filter((r) => !r.test(password)).map((r) => r.label)
    : [];
  const isPasswordStrong = checkPasswordStrong(password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isPasswordStrong) {
      setError('Please fix the password requirements below.');
      return;
    }

    setLoading(true);

    const { data, error } = await signUp(email, password, fullName, {
      company_name: companyName.trim(),
      phone: phone.trim(),
      region_code: regionConfig.code,
      source_site: sessionStorage.getItem('portal_source_site') || regionConfig.sourceSite,
    }, captchaToken);
    setCaptchaNonce((n) => n + 1);

    if (error) {
      // Supabase returns this when email confirmation is ON and the email already exists
      if (error.message?.toLowerCase().includes('already registered') ||
          error.message?.toLowerCase().includes('already exists')) {
        setConfirmSent(true); // silently show "check your email" — same as PHP flow
      } else {
        setError(error.message);
      }
      setLoading(false);
      return;
    }

    // Supabase: empty identities = silent duplicate (email confirmation ON)
    const identities = data?.user?.identities;
    if (identities !== null && identities !== undefined && identities.length === 0) {
      setConfirmSent(true);
      setLoading(false);
      return;
    }

    // --- Trigger admin notification for approval ---
    const accessToken = data?.session?.access_token;
    const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8001';
    if (accessToken) {
      try {
        await fetch(`${apiBase}/api/auth/post-verify`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${accessToken}`, ...getPortalHeaders(regionConfig) },
        });
      } catch (notifyErr) {
        // Optionally log or ignore
      }
    }

    // Supabase returns a session immediately if email confirmation is disabled,
    // or returns a user with no session if confirmation is required.
    if (data?.session) {
      navigate('/onboarding', { replace: true });
    } else {
      setConfirmSent(true);
    }
    setLoading(false);
  };

  const handleResend = async () => {
    setResendStatus('sending');
    const { error } = await supabase.auth.resend({ type: 'signup', email });
    setResendStatus(error ? 'error' : 'sent');
  };

  if (confirmSent) {
    return (
      <div className="w-full max-w-sm mx-auto">
        <h2 className="text-xl font-semibold mb-2">Check your email</h2>
        <p className="text-sm text-muted-foreground mb-4">
          We sent a confirmation link to <span className="font-medium text-foreground">{email}</span>.
          Click the link to activate your account.
        </p>
        {resendStatus === 'sent' && (
          <p className="text-sm text-green-600 mb-3">Confirmation email resent. Please check your inbox.</p>
        )}
        {resendStatus === 'error' && (
          <p className="text-sm text-destructive mb-3">Couldn't resend. Please try again shortly.</p>
        )}
        <button
          onClick={handleResend}
          disabled={resendStatus === 'sending' || resendStatus === 'sent'}
          className="text-sm font-medium text-primary hover:underline block mb-4 disabled:opacity-50"
          data-testid="signup-resend"
        >
          {resendStatus === 'sending' ? 'Sending…' : resendStatus === 'sent' ? 'Email sent!' : "Didn't receive it? Resend"}
        </button>
        <button type="button" onClick={onSwitch} className="text-sm font-medium text-muted-foreground hover:underline" data-testid="signup-back-to-signin">
          Back to sign in
        </button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-sm mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-1">Create your account</h1>
        <p className="text-sm text-muted-foreground">{regionConfig.signupSubtitle}</p>
        <p className="text-xs text-muted-foreground mt-1"><span className="text-destructive">*</span> All fields are required</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div className="space-y-1.5">
          <Label htmlFor="signup-fullName">Full name <span className="text-destructive">*</span></Label>
          <Input id="signup-fullName" type="text" placeholder="Jane Smith" autoComplete="name" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="signup-email">Email address <span className="text-destructive">*</span></Label>
          <Input id="signup-email" type="email" placeholder="you@company.com" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="signup-companyName">Company / organisation name <span className="text-destructive">*</span></Label>
          {regionConfig.code === 'UK' ? (
            // Companies House look-up; whatever is typed is kept, so a practice or non-registered name still works.
            <CompanySearch
              inputId="signup-companyName"
              compact
              minChars={3}
              fetchProfile={false}
              searchFn={searchCompaniesQuietly}
              placeholder="Start typing your company name or number"
              onQueryChange={setCompanyName}
              onSelect={(company) => setCompanyName(company.company_name)}
            />
          ) : (
            <Input id="signup-companyName" type="text" placeholder="Your company or practice" autoComplete="organization" value={companyName} onChange={(e) => setCompanyName(e.target.value)} required />
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="signup-phone">Phone number <span className="text-destructive">*</span></Label>
          <Input id="signup-phone" type="tel" placeholder={regionConfig.phonePlaceholder} autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="signup-password">Password <span className="text-destructive">*</span></Label>
          <div className="relative">
            <Input
              id="signup-password"
              type={showPassword ? 'text' : 'password'}
              placeholder="At least 8 characters"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {password && passwordErrors.length > 0 && (
            <ul className="text-xs text-destructive mt-1.5 space-y-0.5 list-disc list-inside">
              {passwordErrors.map((err) => <li key={err}>{err}</li>)}
            </ul>
          )}
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        {active && <TurnstileWidget key={`signup-${captchaNonce}`} onToken={setCaptchaToken} />}

        <Button
          type="submit"
          className="w-full"
          disabled={loading || !fullName || !companyName || !phone || !email || !isPasswordStrong || isCaptchaPending(captchaToken)}
          data-testid="signup-submit"
        >
          {loading ? 'Creating account…' : 'Create account'}
        </Button>
      </form>

      <p className="text-sm text-muted-foreground mt-5 md:hidden">
        Already have an account?{' '}
        <button type="button" onClick={onSwitch} className="font-medium text-primary hover:underline" data-testid="signup-switch-mobile">
          Sign in
        </button>
      </p>
    </div>
  );
};

export default SignupForm;
